import { create } from 'zustand'
import { User } from 'firebase/auth'
import { Unsubscribe } from 'firebase/firestore'
import { toast } from 'sonner'
import {
  DriverMaster,
  CreateDriverMasterData,
  UpdateDriverMasterData,
  CreateAliasData,
  DriverMasterFilters,
  DriverMasterSortConfig,
  DriverMergeOptions,
  DriverMergePreview
} from '@/types/driverMaster'
import { DriverMasterService } from '@/services/driverMasterService'

interface DeleteDriverOptions {
  deleteDataInOpenPeriods?: boolean
}

interface DeleteDriverResult {
  deletedEarnings: number
  deletedCompensations: number
}

interface DriverDataCounts {
  earningsCount: number
  compensationsCount: number
}

interface DriverMasterStore {
  // State
  drivers: DriverMaster[]
  loading: boolean
  error: string | null
  filters: DriverMasterFilters
  sortConfig: DriverMasterSortConfig

  // Subscription management
  unsubscribe: Unsubscribe | null

  // Actions
  loadDrivers: (user: User) => Promise<void>
  createDriver: (user: User, data: CreateDriverMasterData) => Promise<DriverMaster>
  updateDriver: (user: User, id: string, updates: UpdateDriverMasterData) => Promise<DriverMaster>
  deleteDriver: (user: User, id: string, options?: DeleteDriverOptions) => Promise<DeleteDriverResult>
  getDriverDataInOpenPeriods: (user: User, driverId: string) => Promise<DriverDataCounts>
  createAlias: (user: User, driverId: string, alias: CreateAliasData) => Promise<DriverMaster>
  deleteAlias: (user: User, driverId: string, aliasIndex: number) => Promise<DriverMaster>

  // Merge functionality
  generateMergePreview: (user: User, primaryDriverId: string, secondaryDriverId: string) => Promise<DriverMergePreview>
  mergeDrivers: (user: User, primaryDriverId: string, secondaryDriverId: string, options: DriverMergeOptions) => Promise<DriverMaster>
  
  // UI state management
  setFilters: (filters: Partial<DriverMasterFilters>) => void
  setSortConfig: (sortConfig: DriverMasterSortConfig) => void
  clearError: () => void
  
  // Computed getters
  getFilteredDrivers: () => DriverMaster[]
  getDriverById: (id: string) => DriverMaster | undefined
  
  // Cleanup
  cleanup: () => void
}

export const useDriverMasterStore = create<DriverMasterStore>((set, get) => ({
  // Initial state
  drivers: [],
  loading: false,
  error: null,
  filters: {
    search: '',
    isActive: true
  },
  sortConfig: {
    field: 'primaryName',
    direction: 'asc'
  },
  unsubscribe: null,

  // Load drivers with real-time subscription
  loadDrivers: async (user: User) => {
    try {
      set({ loading: true, error: null })
      
      // Clean up existing subscription
      const { unsubscribe } = get()
      if (unsubscribe) {
        unsubscribe()
      }
      
      // Set up real-time subscription
      const newUnsubscribe = DriverMasterService.subscribeToDrivers(user, (drivers) => {
        set({ drivers, loading: false, error: null })
      })
      
      set({ unsubscribe: newUnsubscribe })
      
    } catch (error) {
      console.error('Failed to load drivers:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att ladda förare'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
    }
  },

  // Create new driver
  createDriver: async (user: User, data: CreateDriverMasterData) => {
    try {
      set({ loading: true, error: null })
      
      const newDriver = await DriverMasterService.createDriver(user, data)
      
      // Note: Real-time subscription will update the drivers list automatically
      set({ loading: false })
      toast.success(`Föraren "${newDriver.primaryName}" har skapats`)
      
      return newDriver
      
    } catch (error) {
      console.error('Failed to create driver:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att skapa föraren'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  },

  // Update existing driver
  updateDriver: async (user: User, id: string, updates: UpdateDriverMasterData) => {
    try {
      set({ loading: true, error: null })
      
      const updatedDriver = await DriverMasterService.editDriver(user, id, updates)
      
      // Note: Real-time subscription will update the drivers list automatically
      set({ loading: false })
      toast.success(`Föraren "${updatedDriver.primaryName}" har uppdaterats`)
      
      return updatedDriver
      
    } catch (error) {
      console.error('Failed to update driver:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att uppdatera föraren'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  },

  // Delete driver (soft delete)
  deleteDriver: async (user: User, id: string, options?: DeleteDriverOptions) => {
    try {
      const { drivers } = get()
      const driver = drivers.find(d => d.id === id)
      const driverName = driver?.primaryName || 'Okänd förare'

      set({ loading: true, error: null })

      const result = await DriverMasterService.deleteDriver(user, id, options)

      // Note: Real-time subscription will update the drivers list automatically
      set({ loading: false })

      // Build success message
      const deletedCount = result.deletedEarnings + result.deletedCompensations
      if (deletedCount > 0) {
        toast.success(`Föraren "${driverName}" har tagits bort tillsammans med ${result.deletedEarnings} intäkter och ${result.deletedCompensations} kompensationer`)
      } else {
        toast.success(`Föraren "${driverName}" har tagits bort`)
      }

      return result

    } catch (error) {
      console.error('Failed to delete driver:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att ta bort föraren'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  },

  // Get driver data counts in open periods (for warning dialog)
  getDriverDataInOpenPeriods: async (user: User, driverId: string) => {
    try {
      return await DriverMasterService.getDriverDataInOpenPeriods(user, driverId)
    } catch (error) {
      console.error('Failed to get driver data counts:', error)
      throw error
    }
  },

  // Create driver alias
  createAlias: async (user: User, driverId: string, alias: CreateAliasData) => {
    try {
      set({ loading: true, error: null })
      
      const updatedDriver = await DriverMasterService.createDriverAlias(user, driverId, alias)
      
      // Note: Real-time subscription will update the drivers list automatically
      set({ loading: false })
      toast.success(`Alias för "${alias.platform}" har lagts till`)
      
      return updatedDriver
      
    } catch (error) {
      console.error('Failed to create alias:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att lägga till alias'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  },

  // Delete driver alias
  deleteAlias: async (user: User, driverId: string, aliasIndex: number) => {
    try {
      const { drivers } = get()
      const driver = drivers.find(d => d.id === driverId)
      const alias = driver?.aliases[aliasIndex]
      const aliasName = alias?.platform || 'Okänt alias'
      
      set({ loading: true, error: null })
      
      const updatedDriver = await DriverMasterService.deleteDriverAlias(user, driverId, aliasIndex)
      
      // Note: Real-time subscription will update the drivers list automatically
      set({ loading: false })
      toast.success(`Alias "${aliasName}" har tagits bort`)
      
      return updatedDriver
      
    } catch (error) {
      console.error('Failed to delete alias:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att ta bort alias'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  },

  // Generate merge preview
  generateMergePreview: async (user: User, primaryDriverId: string, secondaryDriverId: string) => {
    try {
      set({ loading: true, error: null })

      const preview = await DriverMasterService.generateMergePreview(user, primaryDriverId, secondaryDriverId)

      set({ loading: false })
      return preview

    } catch (error) {
      console.error('Failed to generate merge preview:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att generera sammanslagningsförhandsgranskning'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  },

  // Merge two drivers
  mergeDrivers: async (user: User, primaryDriverId: string, secondaryDriverId: string, options: DriverMergeOptions) => {
    try {
      const { drivers } = get()
      const primaryDriver = drivers.find(d => d.id === primaryDriverId)
      const secondaryDriver = drivers.find(d => d.id === secondaryDriverId)
      const primaryName = primaryDriver?.primaryName || 'Okänd förare'
      const secondaryName = secondaryDriver?.primaryName || 'Okänd förare'

      set({ loading: true, error: null })

      const mergedDriver = await DriverMasterService.mergeDrivers(user, primaryDriverId, secondaryDriverId, options)

      // Note: Real-time subscription will update the drivers list automatically
      set({ loading: false })
      toast.success(`Föraren "${secondaryName}" har slagits samman med "${primaryName}"`)

      return mergedDriver

    } catch (error) {
      console.error('Failed to merge drivers:', error)
      const errorMessage = error instanceof Error ? error.message : 'Det gick inte att slå samman förarna'
      set({ loading: false, error: errorMessage })
      toast.error(errorMessage)
      throw error
    }
  },

  // Set search and filter criteria
  setFilters: (newFilters: Partial<DriverMasterFilters>) => {
    set(state => ({
      filters: { ...state.filters, ...newFilters }
    }))
  },

  // Set sort configuration
  setSortConfig: (sortConfig: DriverMasterSortConfig) => {
    set({ sortConfig })
  },

  // Clear error state
  clearError: () => {
    set({ error: null })
  },

  // Get filtered and sorted drivers
  getFilteredDrivers: () => {
    const { drivers, filters, sortConfig } = get()
    
    let filteredDrivers = drivers

    // Apply search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase()
      filteredDrivers = filteredDrivers.filter(driver => 
        driver.primaryName.toLowerCase().includes(searchTerm) ||
        driver.email?.toLowerCase().includes(searchTerm) ||
        driver.phone?.includes(searchTerm) ||
        driver.personalCode?.toLowerCase().includes(searchTerm) ||
        driver.taxiCode?.toLowerCase().includes(searchTerm) ||
        driver.aliases.some(alias => 
          alias.platform.toLowerCase().includes(searchTerm) ||
          alias.name?.toLowerCase().includes(searchTerm) ||
          alias.email?.toLowerCase().includes(searchTerm) ||
          alias.phone?.includes(searchTerm) ||
          alias.uuid?.toLowerCase().includes(searchTerm)
        )
      )
    }

    // Apply platform filter
    if (filters.platform) {
      filteredDrivers = filteredDrivers.filter(driver => 
        driver.aliases.some(alias => alias.platform === filters.platform)
      )
    }

    // Apply active filter
    if (filters.isActive !== undefined) {
      filteredDrivers = filteredDrivers.filter(driver => driver.isActive === filters.isActive)
    }

    // Apply sorting
    filteredDrivers.sort((a, b) => {
      let aValue: any
      let bValue: any
      
      if (sortConfig.field === 'aliasCount') {
        aValue = a.aliases.length
        bValue = b.aliases.length
      } else {
        aValue = a[sortConfig.field as keyof DriverMaster]
        bValue = b[sortConfig.field as keyof DriverMaster]
      }
      
      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1
      
      // Convert to string for comparison if needed
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })

    return filteredDrivers
  },

  // Get driver by ID
  getDriverById: (id: string) => {
    const { drivers } = get()
    return drivers.find(driver => driver.id === id)
  },

  // Cleanup subscriptions
  cleanup: () => {
    const { unsubscribe } = get()
    if (unsubscribe) {
      unsubscribe()
      set({ unsubscribe: null })
    }
  }
}))

// Export hook for easier usage
export const useDriverMaster = () => {
  const store = useDriverMasterStore()
  return {
    ...store,
    filteredDrivers: store.getFilteredDrivers()
  }
}